import { Component } from "projen";
import { NodeProject } from "projen/lib/javascript";
import { JsonFile } from "projen/lib/json";
import { VERSION } from "../versions";

/**
 * Options for the `.api.md` rollup slot produced by API Extractor.
 */
export interface ApiExtractorReportOptions {
  /**
   * Folder (relative to the package root) where the `.api.md` rollup
   * is written when the extractor runs. Treated as a scratch/temp
   * location because rollups are **regenerated on scan** rather than
   * committed to git (per docs-sync epic #448 resolved decision #3).
   *
   * The parent `ApiExtractor` component adds this folder to the
   * package's `.gitignore` so rollups never land on the default branch.
   *
   * @default ".api-extractor/"
   */
  readonly reportFolder?: string;

  /**
   * Filename of the generated rollup, relative to `reportFolder`.
   * Accepts the `<unscopedPackageName>` placeholder API Extractor
   * expands at runtime.
   *
   * @default "<unscopedPackageName>.api.md"
   */
  readonly reportFileName?: string;
}

/**
 * Options for the `ApiExtractor` projen component.
 */
export interface ApiExtractorOptions {
  /**
   * Path to the TypeScript `.d.ts` rollup produced by the package's
   * compile step, relative to the package root. Used as the
   * extractor's `mainEntryPointFilePath`.
   *
   * Defaults to `<projectFolder>/lib/index.d.ts` — matches the
   * `TypeScriptProject` default layout.
   *
   * @default "<projectFolder>/lib/index.d.ts"
   */
  readonly mainEntryPointFilePath?: string;

  /**
   * Path to the generated `api-extractor.json` config file (relative
   * to the package root).
   *
   * @default "api-extractor.json"
   */
  readonly configFilePath?: string;

  /**
   * Rollup report options (where the `.api.md` lands).
   */
  readonly report?: ApiExtractorReportOptions;

  /**
   * Override the version of `@microsoft/api-extractor` that is added
   * to the package's devDeps. Defaults to `VERSION.API_EXTRACTOR_VERSION`.
   */
  readonly apiExtractorVersion?: string;

  /**
   * When `true`, the extractor's `.api.md` rollup is treated as the
   * committed baseline and diff-checked in-place. The docs-sync epic
   * deliberately sets this to `false` — rollups are regenerate-on-scan
   * and not committed to git.
   *
   * @default false
   */
  readonly commitApiReport?: boolean;
}

/**
 * Default folder where `.api.md` rollups are written. Gitignored by
 * the component so rollups never land in a commit.
 */
export const DEFAULT_API_EXTRACTOR_REPORT_FOLDER = ".api-extractor/";

/**
 * Default filename template for rollups. The `<unscopedPackageName>`
 * placeholder is expanded by `@microsoft/api-extractor` at run time.
 */
export const DEFAULT_API_EXTRACTOR_REPORT_FILENAME =
  "<unscopedPackageName>.api.md";

/**
 * Default path (relative to the package root) to the TypeScript
 * `.d.ts` rollup the extractor consumes. Matches the
 * `TypeScriptProject` default output layout.
 */
export const DEFAULT_API_EXTRACTOR_ENTRY_POINT =
  "<projectFolder>/lib/index.d.ts";

/**
 * Default path (relative to the package root) to the generated
 * `api-extractor.json` config file.
 */
export const DEFAULT_API_EXTRACTOR_CONFIG_FILE = "api-extractor.json";

/**
 * Wires `@microsoft/api-extractor` into a TypeScript project so the
 * docs-sync pipeline can detect public-API drift introduced by a
 * diff.
 *
 * The component adds the extractor devDep, synthesizes an
 * `api-extractor.json` under the package root, and gitignores the
 * generated `.api.md` rollup. Rollups are **regenerated on scan**
 * (never committed to git) per docs-sync epic #448 resolved
 * decision #3 — the in-repo helper at
 * `.claude/procedures/extract-api.sh` runs the extractor end-to-end
 * and writes the rollup to the scratch folder for the scan phase
 * to consume in-memory.
 *
 * Callers normally do not construct this directly — the
 * `TypeScriptProject` constructor creates one automatically.
 */
export class ApiExtractor extends Component {
  /**
   * Find the ApiExtractor component on a project.
   */
  public static of(project: NodeProject): ApiExtractor | undefined {
    const isDefined = (c: Component): c is ApiExtractor =>
      c instanceof ApiExtractor;
    return project.components.find(isDefined);
  }

  /**
   * Generated config file path, relative to the package root.
   */
  public readonly configFilePath: string;

  /**
   * Entry-point path supplied to the extractor.
   */
  public readonly mainEntryPointFilePath: string;

  /**
   * Folder where `.api.md` rollups are written.
   */
  public readonly reportFolder: string;

  /**
   * Filename (or filename template) of the rollup.
   */
  public readonly reportFileName: string;

  /**
   * Pinned version of `@microsoft/api-extractor`.
   */
  public readonly apiExtractorVersion: string;

  /**
   * Whether the generated report is committed alongside source.
   * `false` by default — rollups regenerate on scan.
   */
  public readonly commitApiReport: boolean;

  constructor(
    public readonly project: NodeProject,
    options: ApiExtractorOptions = {},
  ) {
    super(project);

    this.configFilePath =
      options.configFilePath ?? DEFAULT_API_EXTRACTOR_CONFIG_FILE;
    this.mainEntryPointFilePath =
      options.mainEntryPointFilePath ?? DEFAULT_API_EXTRACTOR_ENTRY_POINT;
    this.reportFolder =
      options.report?.reportFolder ?? DEFAULT_API_EXTRACTOR_REPORT_FOLDER;
    this.reportFileName =
      options.report?.reportFileName ?? DEFAULT_API_EXTRACTOR_REPORT_FILENAME;
    this.apiExtractorVersion =
      options.apiExtractorVersion ?? VERSION.API_EXTRACTOR_VERSION;
    this.commitApiReport = options.commitApiReport ?? false;

    /**
     * Add the extractor as a devDep. Pinned to a concrete version so
     * every consuming package runs the same rollup logic.
     */
    project.addDevDeps(`@microsoft/api-extractor@${this.apiExtractorVersion}`);

    /**
     * Rollups are regenerate-on-scan by default. Gitignore the
     * scratch folder so no `.api.md` file can land on the default
     * branch by accident.
     */
    if (!this.commitApiReport) {
      const normalizedFolder = this.reportFolder.replace(/\/$/, "");
      project.gitignore.addPatterns(
        `/${normalizedFolder}/`,
        `${normalizedFolder}/**/*.api.md`,
        // Defensive: api-extractor's default `reportTempFolder` is
        // `<projectFolder>/temp/` when not overridden. Our generated
        // config routes that into `reportFolder` already, but a stale
        // extractor or a hand-edited config could still produce a
        // `temp/` folder. Ignore it so rollups never leak into git.
        "/temp/",
      );
      project.npmignore?.addPatterns(`/${normalizedFolder}/`, "/temp/");
    }

    /**
     * `api-extractor.json` is a dev-time config — it doesn't need to
     * ship to npm consumers of the package.
     */
    project.npmignore?.addPatterns(`/${this.configFilePath}`);

    /**
     * Generate `api-extractor.json` so every package can be extracted
     * with a one-liner. Kept minimal — only the knobs the docs-sync
     * scan phase actually reads are turned on.
     *
     * `marker: false` — api-extractor's JSON schema is strict
     * (`additionalProperties: false`) and rejects projen's usual
     * `"//"` generated-by comment. The `linguist-generated` git
     * attribute still flags the file as generated in diffs.
     */
    new JsonFile(project, this.configFilePath, {
      marker: false,
      obj: this.renderConfig(),
    });
  }

  /**
   * Render the JSON body of the generated `api-extractor.json`. Kept
   * as a public method so tests can assert the shape without going
   * through `synthSnapshot`.
   */
  public renderConfig(): Record<string, unknown> {
    return {
      $schema:
        "https://developer.microsoft.com/json-schemas/api-extractor/v7/api-extractor.schema.json",
      projectFolder: ".",
      mainEntryPointFilePath: this.mainEntryPointFilePath,
      bundledPackages: [],
      compiler: {
        tsconfigFilePath: "<projectFolder>/tsconfig.json",
      },
      apiReport: {
        enabled: true,
        reportFolder: `<projectFolder>/${this.reportFolder}`,
        // Route the "temp" rollup copy that api-extractor always
        // writes into the same scratch folder so it inherits the
        // gitignore entry. The default (`<projectFolder>/temp/`)
        // would land beside the source tree and be tracked by git.
        reportTempFolder: `<projectFolder>/${this.reportFolder}`,
        reportFileName: this.reportFileName,
        includeForgottenExports: false,
      },
      docModel: {
        enabled: false,
      },
      dtsRollup: {
        enabled: false,
      },
      tsdocMetadata: {
        enabled: false,
      },
      messages: {
        compilerMessageReporting: {
          default: { logLevel: "warning" },
        },
        extractorMessageReporting: {
          default: { logLevel: "warning" },
          "ae-missing-release-tag": { logLevel: "none" },
        },
        tsdocMessageReporting: {
          default: { logLevel: "warning" },
        },
      },
    };
  }
}
